<template>
  <v-card
    variant="flat"
    class="mb-4 pa-4"
  >
    <template v-if="agreement">
      <h3 class="text-h6 mb-4">
        {{ agreement.title }}
      </h3>
      <v-form ref="form">
        <!-- Agreement HTML is authored and sanitized SERVER-SIDE — the backend
             is the sole sanitization choke point (on save, SD-19-01/02). The
             client renders it verbatim and deliberately does NOT re-sanitize
             (no frontend sanitizer/dependency). No user-supplied HTML ever
             reaches this v-html; the only source is the trusted server payload. -->
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="agreement-content mb-4"
          v-html="agreement.content"
        />
        <!-- eslint-enable vue/no-v-html -->

        <!-- Consent checkbox. There is deliberately no decline/close action:
             this is a hard gate — leaving the checkbox unchecked keeps
             `canProceed` false in OnboardingWizard.vue and the wizard dialog
             stays open (persistent), so the user remains on this step until
             they accept. -->
        <v-checkbox
          v-model="modelValue.consent"
          class="mt-2"
          label="I have read and agree to this agreement"
          :rules="[v => !!v || 'You must agree to this agreement to continue']"
          hide-details="auto"
          required
        />
      </v-form>

      <div class="d-flex align-center justify-space-between ga-3 mt-2">
        <v-btn
          variant="text"
          size="small"
          color="primary"
          prepend-icon="mdi-download"
          class="text-none flex-shrink-0"
          @click="handleDownload"
        >
          Download
        </v-btn>
        <p class="text-caption text-medium-emphasis mb-0 text-right">
          If you have questions, contact {{ brand.supportEmail }}.
        </p>
      </div>
    </template>

    <!-- Required-but-unavailable fallback (SD-19-04 fetch failed / returned
         null while the auth payload still requires acceptance). Without this,
         the wizard's persistent, non-dismissable gate would render an empty
         card with no consent control and no way forward — a blank, unclosable
         modal. Instead we surface an explicit error with a Retry and a support
         contact so the user is never stranded. -->
    <template v-else-if="required">
      <div class="d-flex flex-column align-center text-center py-8">
        <v-icon
          color="warning"
          size="48"
          class="mb-3"
        >
          mdi-alert-circle-outline
        </v-icon>
        <h3 class="text-h6 mb-2">
          We couldn't load the agreement
        </h3>
        <p class="text-body-2 text-medium-emphasis mb-4">
          The required agreement could not be loaded. Please retry. If the
          problem persists, contact {{ brand.supportEmail }}.
        </p>
        <v-btn
          color="primary"
          variant="flat"
          :loading="retrying"
          prepend-icon="mdi-refresh"
          class="text-none"
          @click="handleRetry"
        >
          Retry
        </v-btn>
      </div>
    </template>
  </v-card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { VForm } from 'vuetify/components'
import { useAgreementStore } from '@/stores/agreement'
import { brand } from '@/config/brand'
import { printAgreement } from '@/utils/agreement-print'

export interface IAgreementStepData {
  consent: boolean
}

// `required` is the wizard's presence-driven gate decision
// (`needsAgreementAcceptance`, sourced from the auth payload's
// `click_through_agreement`). When true but the agreement content is
// unavailable, we must show an error/retry state rather than a blank gate.
withDefaults(defineProps<{ required?: boolean }>(), { required: false })

const modelValue = defineModel<IAgreementStepData>('modelValue', { required: true })

const agreementStore = useAgreementStore()

// `getAgreement` fails closed to null (SD-19-04) — nothing renders if the
// deployment has no active agreement, or the payload is malformed.
const agreement = computed(() => agreementStore.getAgreement)

const form = ref<VForm | null>(null)
const retrying = ref(false)

// Re-fetch the active agreement after a failed/empty initial load. Kept local
// to the step so the required-but-unavailable fallback has a self-contained
// recovery path; the store swallows the error and fails closed to null, so we
// simply re-run the fetch and let the `agreement` computed re-render.
async function handleRetry (): Promise<void> {
  retrying.value = true
  try {
    await agreementStore.fetchActiveAgreement()
  } catch (error) {
    console.error('Failed to reload the active agreement:', error)
  } finally {
    retrying.value = false
  }
}

// Let the user save the agreement for their records via the browser's
// print → "Save as PDF" flow (no PDF dependency, no backend round-trip).
function handleDownload (): void {
  const current = agreement.value
  if (!current) return
  printAgreement(current.title, current.content, current.version)
}

defineExpose({ form })
</script>

<style scoped>
.agreement-content {
  min-height: 280px;
  max-height: 55vh;
  overflow-y: auto;
  padding: 16px;
  background-color: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  font-size: 0.875rem;
  line-height: 1.6;
}

/* Prose styling for the server-authored agreement HTML rendered via v-html.
   The global (Vuetify) reset strips list padding, so ordered/unordered list
   markers otherwise hang at the container edge; restore sensible indentation
   and vertical rhythm. :deep() is required to reach the injected markup. */
.agreement-content :deep(p) {
  margin: 0 0 0.75rem;
}

.agreement-content :deep(ol),
.agreement-content :deep(ul) {
  padding-left: 1.5rem;
  margin: 0 0 0.75rem;
}

.agreement-content :deep(li) {
  margin-bottom: 0.375rem;
}

.agreement-content :deep(li > ol),
.agreement-content :deep(li > ul) {
  margin-top: 0.375rem;
  margin-bottom: 0;
}

.agreement-content :deep(blockquote) {
  margin: 0 0 0.75rem;
  padding-left: 1rem;
  border-left: 3px solid rgba(var(--v-border-color), var(--v-border-opacity));
  color: rgb(var(--v-theme-on-surface-variant));
}

.agreement-content :deep(> :last-child),
.agreement-content :deep(li:last-child) {
  margin-bottom: 0;
}
</style>
